/* el-reviews — the review wall's client.
 *
 * WHAT THIS FILE DOES NOT DO, and why that is the design:
 *
 * It does not render the overview. The score, the histogram, the four axes, the tenure module,
 * the summary bullets and the chips are all SERVER-RENDERED from a generated Liquid snippet
 * (snippets/el-reviews-aggregate.liquid, written by scripts/el-reviews-build.mjs). Every prior
 * in-house engine painted that panel from a fetch, which meant a skeleton, a reflow, and the
 * score — the one element the whole section exists for — arriving late. Here it is in the first
 * byte. This file only ever touches the LIST.
 *
 * It also does not re-render the overview when a filter is applied. Filtering to 3 stars does not
 * change what the wall's average is; the panel describes all 1,453 reviews and keeps saying so.
 * Toggling pressed states on the histogram rows and chips is the entire visual response, which
 * costs zero layout shift. The earlier engines re-rendered the whole header on every filter click.
 *
 * So the job here is: fetch the list when it is nearly needed, filter it, sort it, page it, and
 * keep the helpful votes. Progressive enhancement throughout — with JS off, the overview, the
 * summary, the tenure module and the write-a-review form are all still there and correct.
 */
(function () {
  'use strict';

  var MOUNT = document.querySelector('[data-el-reviews]');
  if (!MOUNT || MOUNT.dataset.elBooted) return;
  MOUNT.dataset.elBooted = '1';

  /* PAGINATE (N12): the PDP wall is paged, not endless — Viktor's ruling. Everything else
     (home, /pages/reviews) keeps the append-on-scroll wall, so the two modes share one
     renderer and differ only in whether renderList appends or replaces. */
  var PAGINATE = MOUNT.getAttribute('data-paginate') === '1';
  var PAGE = PAGINATE ? 6 : 8;
  var page = 0;
  var SRC = MOUNT.getAttribute('data-src');
  var EMIT_LD = MOUNT.getAttribute('data-jsonld') === 'true';
  /* one level below the wall heading — h2 on /pages/reviews, h3 where the wall is an h2 */
  var CARD_H = MOUNT.getAttribute('data-card-heading') === 'h2' ? 'h2' : 'h3';
  /* Every visible string comes from the snippet's data-el-rv-i18n block (Liquid is the only place a
     translation exists); the English here is only the fallback when that block is missing. */
  var I18N = {};
  try { I18N = JSON.parse((document.querySelector('[data-el-rv-i18n]') || {}).textContent || '{}'); } catch (e) { I18N = {}; }
  var LANG = I18N.lang || document.documentElement.lang || 'en';
  function tr(key, fallback, map) {
    var s = I18N[key] || fallback;
    Object.keys(map || {}).forEach(function (k) { s = s.split(k).join(String(map[k])); });
    return s;
  }
  function plural(key, fallbackOne, fallbackOther, n) {
    var forms = I18N[key] || { one: fallbackOne, other: fallbackOther };
    var cat = 'other';
    try { cat = new Intl.PluralRules(LANG).select(n); } catch (e) { cat = n === 1 ? 'one' : 'other'; }
    return (forms[cat] || forms.other || fallbackOther).split('__N__').join(num(n));
  }
  var AXES = I18N.axes && I18N.axes.length === 4 ? I18N.axes : ['Feel', 'Whitening', 'Dental health', 'Value'];

  var state = { all: null, star: null, topic: null, sort: 'best', shown: 0, loading: false, view: [] };

  /* Teardown registry. The Theme Editor re-runs section scripts on every settings change, and a
     document-level listener registered on each run is a leak that eventually eats the page.
     Every document listener goes through on() and is flushed on shopify:section:unload. */
  var teardown = [];
  function on(target, ev, fn, opts) {
    target.addEventListener(ev, fn, opts);
    teardown.push(function () { target.removeEventListener(ev, fn, opts); });
  }

  /* localStorage can throw outright — Safari private mode, blocked third-party contexts, a full
     quota. These run inside the per-card render loop, so an unguarded throw kills the whole list
     rather than one vote. Wrapped, always. */
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* no vote memory, still renders */ } }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function num(n) {
    try { return Number(n).toLocaleString(LANG); } catch (e) { return Number(n).toLocaleString(); }
  }
  function words(s) { return String(s).trim().split(/\s+/).length; }

  /* Boxed Trustpilot-style units (N9 §4.1 — one star language everywhere): a filled square
     with the star KNOCKED OUT in the page ground; partials are a width-clipped fill layer. */
  /* the STAR painted in the ground colour over the filled square — square = mark, star = hole */
  var KNOCK_PATH = 'M12 3.6l2.47 5 5.53.8-4 3.9.94 5.5L12 16.2l-4.94 2.6.94-5.5-4-3.9 5.53-.8 2.47-5z';
  function starsHTML(value, cls) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      var fill = Math.max(0, Math.min(1, value - i + 1));
      var knock = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + KNOCK_PATH + '"/></svg>';
      if (fill >= 0.98) {
        out += '<span class="el-rv-su is-on">' + knock + '</span>';
      } else if (fill <= 0.02) {
        out += '<span class="el-rv-su">' + knock + '</span>';
      } else {
        out += '<span class="el-rv-su"><span class="el-rv-su__part" style="width:' + (fill * 100) + '%"></span>' + knock + '</span>';
      }
    }
    return '<span class="el-rv-stars ' + (cls || '') + '" role="img" aria-label="' +
      esc(tr('starsOf5', '__N__ out of 5 stars', { __N__: num(Math.round(value * 10) / 10) })) + '">' + out + '</span>';
  }

  function timeAgo(iso) {
    var then = new Date(iso + 'T00:00:00Z');
    var days = Math.floor((Date.now() - then.getTime()) / 86400000);
    /* the browser words relative dates in the page's language ("3 months ago", "vor 3 Monaten") */
    var rtf = null;
    try { rtf = new Intl.RelativeTimeFormat(LANG, { numeric: 'auto' }); } catch (e) { rtf = null; }
    if (days < 1) return rtf ? rtf.format(0, 'day') : 'today';
    var unit, n;
    if (days < 14) { unit = 'day'; n = days; }
    else if (days < 60) { unit = 'week'; n = Math.floor(days / 7); }
    else if (days < 730) { unit = 'month'; n = Math.floor(days / 30.44); }
    else { unit = 'year'; n = Math.floor(days / 365); }
    if (rtf) {
      try { return new Intl.RelativeTimeFormat(LANG, { numeric: 'always' }).format(-n, unit); } catch (e) { /* fall through */ }
    }
    return n + ' ' + unit + (n === 1 ? '' : 's') + ' ago';
  }

  /* ------------------------------------------------------------ filter + sort */
  function filtered() {
    var list = state.all || [];
    if (state.star) list = list.filter(function (r) { return r.r === state.star; });
    if (state.topic) list = list.filter(function (r) { return (r.tp || []).indexOf(state.topic) > -1; });
    return list;
  }

  /* "Best" is an INFORMATIVENESS ranking, not a rating one. Substance, specificity, a title, a
     verified flag and earned helpful votes all count; the star rating deliberately does not. A
     default sort that floats 5-star reviews is just a nicer-looking filter, and a reader can feel
     it. Length is bucketed rather than scored linearly so the first page stays length-mixed
     instead of becoming five essays. */
  function quality(r) {
    var w = words(r.b);
    var lenScore = w < 10 ? 0.2 : w < 25 ? 0.5 : w < 50 ? 0.85 : w < 100 ? 1 : 0.95;
    var specific = /week|month|photo|dentist|hygienist|coffee|wine|\d/i.test(r.b) ? 0.35 : 0;
    var titled = r.t ? 0.15 : 0;
    var verified = r.v ? 0.1 : 0;
    var voted = Math.min(0.4, (r.u || 0) / 40);
    var subs = (r.sub || []).some(function (v) { return v > 0; }) ? 0.1 : 0;
    return lenScore + specific + titled + verified + voted + subs;
  }
  function lenBucket(r) { var w = words(r.b); return w < 25 ? 0 : w < 75 ? 1 : 2; }

  /* Interleave length buckets so page one is not uniformly long or uniformly short — real walls
     read mixed, and a page of eight 200-word reviews looks curated. */
  function rankBest(list) {
    var sorted = list.slice().sort(function (a, b) {
      var d = quality(b) - quality(a);
      if (d) return d;
      return a.d < b.d ? 1 : a.d > b.d ? -1 : 0;
    });
    var buckets = [[], [], []];
    sorted.forEach(function (r) { buckets[lenBucket(r)].push(r); });
    var out = [];
    var order = [1, 2, 0, 1, 0, 2, 1, 0];
    var i = 0;
    while (out.length < sorted.length) {
      var b = buckets[order[i % order.length]];
      if (b.length) out.push(b.shift());
      else {
        var fallback = buckets.find(function (x) { return x.length; });
        if (!fallback) break;
        out.push(fallback.shift());
      }
      i++;
    }
    /* Hard guard: no sub-3-star review in the first page. They are never hidden — every star row
       and every chip reaches them, and the summary names them — but the default view should not
       open on the tail. */
    var head = out.slice(0, PAGE);
    var rest = out.slice(PAGE);
    var demote = head.filter(function (r) { return r.r < 3; });
    if (demote.length) {
      head = head.filter(function (r) { return r.r >= 3; });
      var promote = rest.filter(function (r) { return r.r >= 3; }).slice(0, demote.length);
      promote.forEach(function (r) { rest.splice(rest.indexOf(r), 1); });
      out = head.concat(promote, demote, rest);
    }
    return out;
  }

  function sorted() {
    var list = filtered();
    switch (state.sort) {
      case 'recent': return list.slice().sort(function (a, b) { return a.d < b.d ? 1 : a.d > b.d ? -1 : 0; });
      case 'high': return list.slice().sort(function (a, b) { return b.r - a.r || quality(b) - quality(a); });
      case 'low': return list.slice().sort(function (a, b) { return a.r - b.r || quality(b) - quality(a); });
      case 'helpful': return list.slice().sort(function (a, b) { return (b.u || 0) - (a.u || 0) || quality(b) - quality(a); });
      default: return rankBest(list);
    }
  }

  /* ------------------------------------------------------------ card */
  var ICO_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
  var ICO_THUMB = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2 9.5h3.5V21H2zM22 11a2 2 0 0 0-2-2h-5.1l.8-3.9a1.6 1.6 0 0 0-.42-1.45L14.5 3 8.4 9.2a2 2 0 0 0-.6 1.42V19a2 2 0 0 0 2 2h7.4a2 2 0 0 0 1.86-1.27l2.2-6.2A2 2 0 0 0 22 11z"/></svg>';

  function axesHTML(r) {
    if (!r.sub || !r.sub.some(function (v) { return v > 0; })) return '';
    /* One combined screen-reader label for the block rather than four near-identical ones — the
       dots are a visual summary, and four separate "4 out of 5" announcements per card is noise
       on a page with hundreds of cards. */
    var label = tr('categoryRatings', 'Category ratings') + ': ' + AXES.map(function (a, i) {
      return r.sub[i] ? tr('axisScore', '__A__ __N__ out of 5', { __A__: a, __N__: num(r.sub[i]) }) : null;
    }).filter(Boolean).join(', ');
    var rows = AXES.map(function (a, i) {
      var v = r.sub[i] || 0;
      if (!v) return '';
      var dots = '';
      for (var d = 1; d <= 5; d++) dots += '<i class="el-rv-cax__dot' + (d <= v ? ' is-on' : '') + '"></i>';
      return '<span class="el-rv-cax"><span>' + a + '</span><span class="el-rv-cax__dots">' + dots + '</span></span>';
    }).join('');
    return '<div class="el-rv-card__axes" role="img" aria-label="' + esc(label) + '">' + rows + '</div>';
  }

  function cardHTML(r) {
    var voted = lsGet('el_rv_v_' + r.id);
    return '<article class="el-rv-card" data-id="' + esc(r.id) + '">' +
      '<div class="el-rv-card__top">' + starsHTML(r.r) +
        '<span class="el-rv-card__meta">' + esc(timeAgo(r.d)) + '</span></div>' +
      (r.t ? '<' + CARD_H + ' class="el-rv-card__title">' + esc(r.t) + '</' + CARD_H + '>' : '') +
      '<p class="el-rv-card__body">' + esc(r.b) + '</p>' +
      axesHTML(r) +
      '<div class="el-rv-card__foot">' +
        '<span class="el-rv-card__who">' +
          '<span class="el-rv-card__author">' + esc(r.a) + '</span>' +
          (r.v ? '<span class="el-rv-card__vbadge">' + ICO_CHECK + esc(tr('verified', 'Verified purchase')) + '</span>' : '') +
        '</span>' +
        '<span class="el-rv-helpful"><span>' + esc(tr('helpful', 'Helpful?')) + '</span>' +
          '<button type="button" class="el-rv-h' + (voted === 'up' ? ' is-sel' : '') + '" data-v="up" data-id="' + esc(r.id) +
            '" aria-pressed="' + (voted === 'up') + '" aria-label="' + esc(tr('helpfulUp', 'Mark this review helpful')) + '">' + ICO_THUMB +
            '<span>' + (r.u || 0) + '</span></button>' +
          '<button type="button" class="el-rv-h el-rv-h--down' + (voted === 'down' ? ' is-sel' : '') + '" data-v="down" data-id="' + esc(r.id) +
            '" aria-pressed="' + (voted === 'down') + '" aria-label="' + esc(tr('helpfulDown', 'Mark this review not helpful')) + '">' + ICO_THUMB +
            '<span>' + (r.dn || 0) + '</span></button>' +
        '</span>' +
      '</div></article>';
  }

  /* ------------------------------------------------------------ render */
  var listEl, noteEl, moreEl;

  function renderNote() {
    var total = state.view.length;
    var bits = [];
    if (state.star) bits.push(tr('starFilter', '__N__-star', { __N__: state.star }));
    if (state.topic) {
      var chip = MOUNT.querySelector('.el-rv-chip[data-topic="' + state.topic + '"]');
      if (chip) bits.push('“' + chip.getAttribute('data-label') + '”');
    }
    noteEl.textContent = bits.length
      ? plural('showing', 'Showing __N__ review', 'Showing __N__ reviews', total) + ' · ' + bits.join(' · ')
      : plural('count', '__N__ review', '__N__ reviews', total);
    var clear = MOUNT.querySelector('.el-rv-clear');
    if (clear) clear.hidden = !bits.length;
  }

  function renderList(reset) {
    if (reset) {
      state.view = sorted();
      state.shown = 0;
      page = 0;
      listEl.innerHTML = '';
    }
    if (!state.view.length) {
      listEl.innerHTML = '<p class="el-rv-empty">' + esc(tr('noMatch', 'No reviews match that combination.')) +
        '<br><button type="button" data-clear="1">' + esc(tr('clear', 'Clear filters')) + '</button></p>';
      moreEl.hidden = true;
      if (pagerEl) pagerEl.hidden = true;
      if (statusEl) statusEl.hidden = true;
      renderNote();
      return;
    }
    if (PAGINATE) {
      /* replace, don't append: a page IS the list */
      listEl.innerHTML = '';
      state.shown = page * PAGE;
    }
    var slice = state.view.slice(state.shown, state.shown + PAGE);
    /* Append-only via a fragment: one reflow per page instead of one per card, and the cards
       already on screen are never re-created (so their vote state and focus survive). */
    var frag = document.createDocumentFragment();
    var tmp = document.createElement('div');
    slice.forEach(function (r) {
      tmp.innerHTML = cardHTML(r);
      frag.appendChild(tmp.firstChild);
    });
    listEl.appendChild(frag);
    state.shown += slice.length;
    moreEl.hidden = PAGINATE || state.shown >= state.view.length;
    moreEl.textContent = tr('moreLeft', 'Show more reviews (__N__ left)', { __N__: num(state.view.length - state.shown) });
    /* Re-arm the pager here, not only inside the observer callback. A filter that narrows the
       list to a single page hides the button, and the callback's re-arm deliberately skips a
       hidden element — so without this line, clearing that filter restored 1,453 reviews but
       left the observer permanently detached and paging silently dead. Observing an element
       that is already observed is a no-op, so this is safe to call on every render. */
    if (io && !moreEl.hidden) io.observe(moreEl);
    if (PAGINATE) renderPager();
    renderNote();
  }

  /* ------------------------------------------------------------ pager (PDP) */
  function pageCount() {
    return Math.max(1, Math.ceil(state.view.length / PAGE));
  }

  /* 1 2 3 … N with the current page always flanked — the classic elided run, so a
     1,453-review wall never prints 243 buttons. */
  function pageNumbers(total, current) {
    if (total <= 7) {
      var all = [];
      for (var i = 0; i < total; i++) all.push(i);
      return all;
    }
    var out = [0];
    var from = Math.max(1, current - 1);
    var to = Math.min(total - 2, current + 1);
    if (from > 1) out.push('gap');
    for (var j = from; j <= to; j++) out.push(j);
    if (to < total - 2) out.push('gap');
    out.push(total - 1);
    return out;
  }

  function renderPager() {
    if (!pagerEl) return;
    var total = pageCount();
    pagerEl.hidden = total < 2;
    if (statusEl) {
      statusEl.hidden = total < 2;
      statusEl.textContent = tr('pageOf', 'Page __P__ of __T__', { __P__: num(page + 1), __T__: num(total) });
    }
    if (total < 2) return;

    pagesEl.innerHTML = pageNumbers(total, page).map(function (n) {
      if (n === 'gap') return '<span class="el-rv-pager__gap" aria-hidden="true">…</span>';
      return '<button type="button" class="el-rv-pager__n' + (n === page ? ' is-on' : '') +
        '" data-el-rv-page="' + n + '"' + (n === page ? ' aria-current="page"' : '') +
        '>' + (n + 1) + '</button>';
    }).join('');

    prevEl.disabled = page === 0;
    nextEl.disabled = page >= total - 1;
  }

  function goToPage(n) {
    var total = pageCount();
    page = Math.min(Math.max(0, n), total - 1);
    renderList(false);
    /* Land the reader on the CONTROLS row — the sort/filter line directly above the list —
       not on the top of the mount. The mount opens with the overview card, and on the
       standalone wall the furniture between the mount's top and the first review measured
       1,849px at 375, so `MOUNT.top - 90` left the new page's first review 1,939px below the
       fold and the pager looked like it had done nothing. Measured 2026-09-19.
       The 90px magic number is gone with it; the offset is now scroll-margin-top on
       .el-rv-controls, which reads the header tokens (--el-header-h / -mobile) instead of
       guessing, and stays correct if the header's height ever changes. */
    var anchor = MOUNT.querySelector('.el-rv-controls') || MOUNT;
    anchor.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /* ------------------------------------------------------------ paging */
  var io = null;
  var pagerEl, pagesEl, prevEl, nextEl, statusEl;
  function observePaging() {
    /* paginate mode never auto-loads: the pager IS the control */
    if (PAGINATE) { if (moreEl) moreEl.hidden = true; return; }
    if (!('IntersectionObserver' in window) || !moreEl) return; // the button remains the fallback
    /* The observed element is the "show more" BUTTON, not a sentinel div, and that is deliberate.
       The first version watched an empty <div class="el-rv-sentinel">, which Dawn's base stylesheet
       collapses via `div:empty { display: none }` — a zero-box element never intersects anything,
       so the observer never fired once and the list stopped dead after page one with no error to
       show for it. Beating that rule needs a specificity fight; observing the button instead needs
       nothing. The button also carries text, so it can never be caught by :empty, and it is
       already exactly the "reader has reached the end of the list" marker. */
    io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting || moreEl.hidden) return;
      renderList(false);
      /* RE-ARM. An IntersectionObserver only fires when an element CROSSES the threshold, not
         while it stays past it. Appending a page below a sentinel that is already in view moves
         the sentinel further down but never un-intersects it, so no second callback ever comes
         and the list stops dead after one page — which is exactly what shipped on the first pass
         and what the capture script caught. Dropping and re-adding the observation forces a fresh
         evaluation on the next frame, after layout has settled. */
      io.unobserve(moreEl);
      requestAnimationFrame(function () {
        if (io && !moreEl.hidden) io.observe(moreEl);
      });
    }, { rootMargin: '600px 0px' });
    io.observe(moreEl);
    teardown.push(function () { io.disconnect(); io = null; });
  }

  /* ------------------------------------------------------------ load */
  function boot() {
    listEl = MOUNT.querySelector('[data-el-rv-list]');
    noteEl = MOUNT.querySelector('[data-el-rv-note]');
    moreEl = MOUNT.querySelector('[data-el-rv-more]');
    pagerEl = MOUNT.querySelector('[data-el-rv-pager]');
    pagesEl = MOUNT.querySelector('[data-el-rv-pages]');
    prevEl = MOUNT.querySelector('[data-el-rv-prev]');
    nextEl = MOUNT.querySelector('[data-el-rv-next]');
    statusEl = MOUNT.querySelector('[data-el-rv-pagestatus]');
    if (!listEl) return;

    if (state.loading || state.all) return;
    state.loading = true;
    fetch(SRC, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        state.all = json.reviews || [];
        renderList(true);
        observePaging();
        if (EMIT_LD) injectJsonLd();
      })
      .catch(function () {
        /* The overview above is server-rendered and stays correct, so a failed list fetch degrades
           to "the score and summary, without the individual reviews" rather than to an empty band. */
        listEl.innerHTML = '<p class="el-rv-empty">' + esc(tr('loadFailed', 'The individual reviews could not load right now. The rating and summary above are the full set.')) + '</p>';
        moreEl.hidden = true;
      })
      .finally(function () { state.loading = false; });
  }

  /* Fetch when the wall nears the viewport, not on page load — this is a 400 KB asset and it must
     never compete with the product image for bandwidth on a PDP. IntersectionObserver is correct
     here precisely because this is a reveal-once concern (scroll-POSITION UI would want rAF). */
  if ('IntersectionObserver' in window) {
    var bootIo = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { bootIo.disconnect(); boot(); }
    }, { rootMargin: '900px 0px' });
    bootIo.observe(MOUNT);
    teardown.push(function () { bootIo.disconnect(); });
  } else {
    boot();
  }

  /* ------------------------------------------------------------ interaction */
  MOUNT.addEventListener('click', function (e) {
    var hrow = e.target.closest('.el-rv-hrow');
    if (hrow) {
      var s = +hrow.getAttribute('data-star');
      state.star = state.star === s ? null : s;
      MOUNT.querySelectorAll('.el-rv-hrow').forEach(function (b) {
        b.setAttribute('aria-pressed', String(+b.getAttribute('data-star') === state.star));
      });
      renderList(true);
      return;
    }
    var chip = e.target.closest('.el-rv-chip');
    if (chip) {
      var t = chip.getAttribute('data-topic');
      state.topic = state.topic === t ? null : t;
      MOUNT.querySelectorAll('.el-rv-chip').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-topic') === state.topic));
      });
      renderList(true);
      return;
    }
    if (e.target.closest('[data-clear]')) {
      state.star = null;
      state.topic = null;
      MOUNT.querySelectorAll('.el-rv-hrow,.el-rv-chip').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
      renderList(true);
      return;
    }
    if (e.target.closest('[data-el-rv-more]')) { renderList(false); return; }
    var pageBtn = e.target.closest('[data-el-rv-page]');
    if (pageBtn) { goToPage(parseInt(pageBtn.getAttribute('data-el-rv-page'), 10)); return; }
    if (e.target.closest('[data-el-rv-prev]')) { goToPage(page - 1); return; }
    if (e.target.closest('[data-el-rv-next]')) { goToPage(page + 1); return; }
    var h = e.target.closest('.el-rv-h');
    if (h) vote(h);
  });

  MOUNT.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-el-rv-sort]');
    if (sel) { state.sort = sel.value; renderList(true); }
  });

  /* One vote per review per browser, toggleable, and it only ever moves the number the reader
     sees — nothing is posted anywhere. The counts in the data are what other people found
     useful; this is the reader adding their own, locally. */
  function vote(btn) {
    var id = btn.getAttribute('data-id');
    var dir = btn.getAttribute('data-v');
    var key = 'el_rv_v_' + id;
    var prev = lsGet(key);
    var wrap = btn.parentNode;
    var up = wrap.querySelector('.el-rv-h[data-v="up"] span');
    var down = wrap.querySelector('.el-rv-h[data-v="down"] span');
    var rec = (state.all || []).find(function (r) { return r.id === id; });
    if (!rec) return;

    if (prev === dir) { lsDel(key); }
    else { lsSet(key, dir); }
    var now = lsGet(key);

    up.textContent = (rec.u || 0) + (now === 'up' ? 1 : 0);
    down.textContent = (rec.dn || 0) + (now === 'down' ? 1 : 0);
    wrap.querySelectorAll('.el-rv-h').forEach(function (b) {
      var on = b.getAttribute('data-v') === now;
      b.classList.toggle('is-sel', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }

  /* ------------------------------------------------------------ JSON-LD */
  /* OFF unless the section explicitly turns it on. Emitting AggregateRating markup for a review
     set that is not yet collected from real verified buyers is a Google Merchant Center problem,
     not a nice-to-have — a product feed can be suspended over it. The setting exists so it can be
     switched on the day the reviews are real, and it defaults to off so it cannot be switched on
     by forgetting. */
  function injectJsonLd() {
    if (!state.all || !state.all.length) return;
    var agg = MOUNT.getAttribute('data-ld-product');
    if (!agg) return;
    var top = state.all.slice().sort(function (a, b) { return quality(b) - quality(a); }).slice(0, 12);
    var mean = state.all.reduce(function (a, r) { return a + r.r; }, 0) / state.all.length;
    var node = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': agg,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Math.round(mean * 10) / 10,
        reviewCount: state.all.length,
        bestRating: 5,
        worstRating: 1,
      },
      review: top.map(function (r) {
        return {
          '@type': 'Review',
          author: { '@type': 'Person', name: r.a },
          datePublished: r.d,
          reviewBody: r.b,
          name: r.t || undefined,
          reviewRating: { '@type': 'Rating', ratingValue: r.r, bestRating: 5, worstRating: 1 },
        };
      }),
    };
    var tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.textContent = JSON.stringify(node);
    document.head.appendChild(tag);
    teardown.push(function () { tag.remove(); });
  }

  /* ------------------------------------------------------------ editor lifecycle */
  on(document, 'shopify:section:unload', function (e) {
    if (!e.target.contains(MOUNT)) return;
    teardown.forEach(function (fn) { try { fn(); } catch (err) {} });
    teardown = [];
    delete MOUNT.dataset.elBooted;
  });
})();
